// dashboard/src/pages/apps-setup/UnityVersionPanel.tsx
// The app's Unity version (api lib/unity-versions.ts): drawn at random from
// its repo type's versions at the first build, changeable here to any version
// of that type. A change applies from the next build; builds already made
// keep their own copy. Versions out of the draw are for test builds.
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../api/client';
import { setAppUnityVersion, type AppUnityStatus } from '../../api/app-builds';

const SOURCE_LABELS: Record<string, string> = {
  random: 'assigned at random',
  manual: 'set by hand',
  moved: 'moved to base',
  backfill: 'set for existing apps',
};

function fmtDay(s: number): string {
  return new Date(s * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function UnityBadges({ isBase, isLts }: { isBase: boolean; isLts: boolean }) {
  return (
    <>
      {isBase && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-primary/15 text-primary">Base</span>}
      {isLts && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-green-500/15 text-green-500">LTS</span>}
    </>
  );
}

export function UnityVersionPanel({ appId, unity }: { appId: string; unity: AppUnityStatus | undefined }) {
  const qc = useQueryClient();
  const [picked, setPicked] = useState('');
  useEffect(() => { setPicked(unity?.version ?? ''); }, [unity?.version]);

  const save = useMutation({
    mutationFn: () => setAppUnityVersion(appId, picked),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['build-config', appId] });
      qc.invalidateQueries({ queryKey: ['unity-versions'] });
      qc.invalidateQueries({ queryKey: ['repo-types'] });
    },
  });

  const options = unity?.options ?? [];
  const inDraw = options.filter((v) => v.in_draw);
  // Out of the draw: offered for test builds only, new apps never draw them.
  const test = options.filter((v) => !v.in_draw);
  const pickedTest = test.some((v) => v.version === picked);
  const current = unity?.version ?? null;
  const typeName = unity?.type?.name;

  return (
    <section className="bg-surface border border-border rounded-lg p-4 space-y-3">
      <h3 className="font-medium text-content">Unity version</h3>
      {current ? (
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <code className="text-content text-sm">{current}</code>
            <UnityBadges isBase={!!unity?.is_base} isLts={!!unity?.is_lts} />
            {unity?.type && !unity.on_type && (
              <span className="text-red-500 text-xs">not on {typeName}'s list — pick another one below</span>
            )}
            {unity?.on_type && !unity.in_draw && (
              <span className="text-yellow-500 text-xs">out of {typeName}'s draw — a test version</span>
            )}
          </div>
          <p className="text-content-subtle text-xs">
            Unity {unity?.line ?? '?'}
            {unity?.source && <> · {SOURCE_LABELS[unity.source] ?? unity.source}{unity.assigned_at ? ` on ${fmtDay(unity.assigned_at)}` : ''}</>}
            {' · '}{unity?.builds_with_it ?? 0} build{unity?.builds_with_it === 1 ? '' : 's'} with it
          </p>
        </div>
      ) : (
        <p className="text-content-subtle text-sm">
          {typeName
            ? `Not assigned yet — one is drawn at random from ${typeName}'s versions at the first build.`
            : 'Not assigned yet — pick a base repository with a type first.'}
        </p>
      )}
      {unity?.type && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-content-subtle text-xs">Change version</span>
            <select
              value={picked}
              onChange={(e) => setPicked(e.target.value)}
              className="px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content"
            >
              {!current && <option value="">—</option>}
              {current && !unity.on_type && <option value={current}>{current} · not on {typeName}</option>}
              <optgroup label="In the draw">
                {inDraw.map((v) => (
                  <option key={v.version} value={v.version}>
                    {v.version} · Unity {v.line}{v.is_lts ? ' LTS' : ''}{v.is_base ? ' · base' : ''}
                  </option>
                ))}
              </optgroup>
              {test.length > 0 && (
                <optgroup label="Out of the draw — test builds only">
                  {test.map((v) => (
                    <option key={v.version} value={v.version}>
                      {v.version} · Unity {v.line}{v.is_lts ? ' LTS' : ''} · test
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </label>
          <button
            type="button"
            disabled={!picked || picked === current || save.isPending}
            onClick={() => save.mutate()}
            className="px-3 py-1.5 text-sm rounded-md bg-primary text-white hover:opacity-90 disabled:opacity-40"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
      <p className="text-xs text-content-subtle">
        Applies from the next build. Builds already made keep their version.
        {typeName && <> The list is {typeName}'s, set on the Repos Setup tab.</>}
      </p>
      {pickedTest && picked !== current && (
        <p className="text-xs text-yellow-500">This version is out of the draw: use it for a test build, then put it in the draw on the Repos Setup tab once the build passes.</p>
      )}
      {save.error && <p className="text-red-500 text-sm">{(save.error as ApiError).message}</p>}
    </section>
  );
}
